import { User, UserSettings } from "../interfaces/interfaces";
import { initialsRegex, USER_LOCALSTORAGE_NAME } from "../constants";
import { UserDataResponse } from "../interfaces/responseModels";

export const defaultSettings: UserSettings = {
  approveOpenNextDocument: false,
  holdOpenNextDocument: false,
  rejectOpenNextDocument: false,
  enableTransitionView: false,
  enableAnimations: true,
  hideNonRequiredEntities: false,
  hideAnnotationBoxes: false,
  saveFilters: false,
  saveSorter: false,
  language: "en",
  overviewSorter: "",
  archiveSorter: "",
  overviewFilters: [],
  archiveFilters: [],
  overviewOrderColumns: [],
  archiveOrderColumns: [],
  showOverviewTour: false,
  showDetailTour: false,
};

const setValue = <T>(obj: T, key: keyof T, value: T[keyof T]) => {
  obj[key] = value;
};

export const getUserFromLocalStorage = (): User | null => {
  const user = localStorage.getItem(USER_LOCALSTORAGE_NAME);
  return user ? JSON.parse(user) : null;
};

export const parseSettings = (responseData: UserSettings): UserSettings => {
  if (responseData) {
    Object.keys(defaultSettings).forEach((key: string) => {
      if (!Object.prototype.hasOwnProperty.call(responseData, key)) {
        const keyType = key as keyof UserSettings;
        setValue(responseData, keyType, defaultSettings[keyType]);
      }
    });

    return responseData;
  }

  return defaultSettings;
};

export const getInitialsFromName = (
  firstName: string,
  lastName: string,
): string => {
  const initials = [...`${firstName} ${lastName}`.matchAll(initialsRegex)];
  return (
    (initials.shift()?.[1] || "") + (initials.pop()?.[1] || "")
  ).toUpperCase();
};

export const removeUnknownInputIdsFromFilters = (
  inputPermissions: Array<string>,
  filters: Array<string>,
) => {
  if (!filters?.length || !inputPermissions?.length) {
    return [];
  }

  const inputPermissionsLowerCase = inputPermissions.map((permission) =>
    permission.toLowerCase(),
  );
  const inputIdFilter = filters.find((filter) => filter.includes("input.id"));

  if (!inputIdFilter) {
    return filters;
  }

  const ids = inputIdFilter.replace("input.id=", "").split("+");
  const allowedInputIds = ids.filter((id) =>
    inputPermissionsLowerCase.includes(id.toLowerCase()),
  );

  if (!allowedInputIds.length) {
    return filters.filter((filter) => !filter.includes("input.id"));
  }

  const newInputIdFilter = `input.id=${allowedInputIds.join("+")}`;
  return filters.map((filter) =>
    filter.includes("input.id") ? newInputIdFilter : filter,
  );
};

/**
 * Constructs the avatar URL for a given user ID.
 * Optionally appends a cache-busting timestamp.
 */
export const getAvatarUrl = (userId: string, bustCache = false): string => {
  const baseUrl = `/api/v1/user/${encodeURIComponent(userId)}/avatar`;
  return bustCache ? `${baseUrl}?t=${Date.now()}` : baseUrl;
};

export const getDefaultAvatarForUser = (userData: UserDataResponse): string => {
  // For users with an external picture URL (e.g., Gravatar), use it as default
  if (userData.picture && userData.picture.startsWith("http")) {
    return userData.picture;
  }

  // For all other users, return initials
  const firstName = userData.userProfile?.firstName || "";
  const lastName = userData.userProfile?.lastName || "";
  return `${firstName.charAt(0).toUpperCase()}${lastName
    .charAt(0)
    .toUpperCase()}`;
};
